import httpClient from '../httpClient';

// ===== INTERFACES =====

export interface ApiKey {
  uuid: string;
  name: string;
  key_prefix: string;
  allowed_routes: string[];
  status: 'active' | 'revoked';
  expires_at: string | null;
  last_used_at: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface ApiKeyCreateResponse {
  uuid: string;
  name: string;
  key: string; // raw key — shown only once
  key_prefix: string;
  allowed_routes: string[];
  expires_at: string | null;
}

export interface AvailableRoute {
  path: string;
  methods: string[];
}

export interface Pagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  pagination?: Pagination;
}

// ===== SERVICE FUNCTIONS =====

export const getApiKeys = async (
  page = 1,
  limit = 20,
  status?: string
): Promise<{
  success: boolean;
  data?: ApiKey[];
  pagination?: Pagination;
  error?: { message: string } | null;
}> => {
  try {
    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('limit', String(limit));
    if (status) params.append('status', status);

    const response = await httpClient.get<ApiResponse<ApiKey[]>>(
      `/admin/api-keys?${params}`
    );

    return {
      success: true,
      data: response.data || [],
      pagination: response.pagination,
      error: null,
    };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch API keys' },
    };
  }
};

export const getApiKey = async (
  uuid: string
): Promise<{
  success: boolean;
  data?: ApiKey;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<ApiKey>>(
      `/admin/api-keys/${uuid}`
    );
    return { success: true, data: response.data, error: null };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch API key' },
    };
  }
};

export const createApiKey = async (data: {
  name: string;
  allowed_routes: string[];
  expires_at?: string | null;
}): Promise<{
  success: boolean;
  data?: ApiKeyCreateResponse;
  message?: string;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.post<ApiResponse<ApiKeyCreateResponse>>(
      '/admin/api-keys',
      data
    );
    return {
      success: true,
      data: response.data,
      message: response.message,
      error: null,
    };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to create API key' },
    };
  }
};

export const updateApiKey = async (
  uuid: string,
  data: {
    name?: string;
    allowed_routes?: string[];
    expires_at?: string | null;
  }
): Promise<{
  success: boolean;
  data?: ApiKey;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.put<ApiResponse<ApiKey>>(
      `/admin/api-keys/${uuid}`,
      data
    );
    return { success: true, data: response.data, error: null };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to update API key' },
    };
  }
};

export const revokeApiKey = async (
  uuid: string
): Promise<{
  success: boolean;
  message?: string;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.patch<ApiResponse<null>>(
      `/admin/api-keys/${uuid}/revoke`
    );
    return { success: true, message: response.message, error: null };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to revoke API key' },
    };
  }
};

export const activateApiKey = async (
  uuid: string
): Promise<{
  success: boolean;
  message?: string;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.patch<ApiResponse<null>>(
      `/admin/api-keys/${uuid}/activate`
    );
    return { success: true, message: response.message, error: null };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to activate API key' },
    };
  }
};

export const deleteApiKey = async (
  uuid: string
): Promise<{
  success: boolean;
  message?: string;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.delete<ApiResponse<null>>(
      `/admin/api-keys/${uuid}`
    );
    return { success: true, message: response.message, error: null };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to delete API key' },
    };
  }
};

export const getAvailableRoutes = async (): Promise<{
  success: boolean;
  data?: AvailableRoute[];
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<AvailableRoute[]>>(
      '/admin/api-keys/available-routes'
    );
    return { success: true, data: response.data || [], error: null };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch available routes' },
    };
  }
};
